

/*
 * Date: 2025
 * Description: Main research page for parts
 * Author: Philippe Leroux @ skitsc
 */

//Modules
import { ReactElement , JSX, useEffect, useState , useRef , useContext, useLayoutEffect } from "react"
import { Box, Grid, IconButton , CircularProgress } from '@mui/material'
import { useNavigate } from "react-router-dom"

//Components
import SideBar from "../components/ui/side.bar"
import PartCard from "../components/card/part.card"
import TopPartsContainer from "../components/container/top.parts.container"
import UserPartCard from "../components/card/user.part.card"

//Interfaces
import { i_makes, i_models, i_part_search, i_parts, } from "../interfaces/items.interface"
import { i_side_bar_bottom_props , i_side_bar_props , i_part_card_props , i_top_container_parts } from "../interfaces/display.interfaces"
import { i_child_props, i_filter, i_promise, i_socket_response } from "../interfaces/utility.interface"

//Utility functions
import { handleFetch , findModel } from "../api/fetch"
import { delay, encodeQueryData } from "../utils/utility"

//Constants
import { default_search } from "../utils/constant"

//Icons
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { OpenInFull } from "@mui/icons-material"

//Context
import { useSocketEvent } from "../context/socket.context"
import { MainContext, ThemeContext } from "../context/context"
import { useAlert } from "../context/alert.context"


const Catalog = ( props : i_child_props ) :  JSX.Element | ReactElement => {
    const { HandleLogout , user } = useContext(MainContext)
    const [ filter, setFilter ] = useState<i_filter>(default_search)
    const [ isNearBottom, setIsNearBottom ] = useState<boolean>(false);
    const nav = useNavigate();
    const listRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const [ hideSide , setHideSide ] = useState<boolean>(false)
    const [ makes , setMakes ] = useState<i_makes[]>([])
    const [ models, setModels ] = useState<i_models[]>([])
    const [ years, setYears ] = useState<number[]>([])
    const [ part_search , setPartSearch ] = useState<i_part_search[]>([])
    const [ search_filter ] = useState<i_filter>(default_search)
    const [ sku , setSku ] = useState<string>("")
    const [ parts, setParts] = useState<i_parts[]>([])
    const [ target , setTarget ] = useState<i_parts | null>(null)
    const [ parts_count , setPartsCount ] = useState<number>(0)
    const [ loading , setLoading ] = useState<boolean>(false)
    const { mode } = useContext(ThemeContext)
    //Hooks
    const { showAlert } = useAlert();
    useSocketEvent('search', (output : i_socket_response) => {
        if(output.type === 'Add'){
            const find = part_search.find(( item : i_part_search ) => item._id === output.item._id)
            if(!find) setPartSearch([output.item ,...part_search ])
        }
    })
    useEffect(() => {
        setLoading(true)
        const getMakes = async() => {
            const res : i_promise = await handleFetch('/makes' , 'GET' , true , null)
            await getSearchs()
            if(res.type === 'Success'){
                setMakes(res.data)
            }
            if(res.type === 'Unauthorized'){
                HandleLogout(nav)
            }
            if(res.type === 'Error' || res.type === 'Failed'){
                showAlert(res)
            }
            setLoading(false)
        }
        const getSearchs = async() => {
            const params : URLSearchParams = encodeQueryData(search_filter)
            const res : i_promise = await handleFetch('/searchs?'+params, 'GET', true, null)
            if(res.type === 'Success'){
                setPartSearch(res.data)
            }else{
                showAlert(res)
            }
        }
        getMakes()
    },[])
    useEffect(() => {
        const getModels = async() => {
            setFilter({...filter , model_id : '' , year : '' , sku : ''})
            setYears([])
            setParts([])
            setSku('')
            if(filter.make_id){
                setLoading(true)
                const res = await handleFetch(`/models?make_id=${filter.make_id}`, 'GET', true, null)
                if(res.type === 'Success'){
                    setModels(res.data)
                }else{
                    showAlert(res)
                }
                setLoading(false)
            }
        }
        getModels()
    },[filter.make_id])
    useEffect(() => {
        if(filter.model_id !== ''){
            setSku('')
            setParts([])
            setFilter({...filter, year : ''})
            const selected_model = models.find(model => model._id  === filter.model_id)
            if(selected_model)setYears(selected_model?.years)
        }
    },[filter.model_id])
    useEffect(() => {
        const getParts = async() => {
            setLoading(true)
            await delay(500)
            const res = await handleFetch('/partsv2?make_id='+filter.make_id+'&model_id='+filter.model_id+'&year='+filter.year+'&search='+filter.search, 'GET', true, null)
            if(res.type === 'Success'){
                setParts(res.data.parts)
                setPartsCount(res.data.count)
            }else{
                showAlert(res)
            }
            setLoading(false)
        }
        if(filter.year !== '' && filter.make_id !== '' && filter.model_id !== ''){
            getParts()
        }
    },[filter.year])
    useEffect(() => {
        const getParts = async() => {
            setLoading(true)
            await delay(500)
            const res = await handleFetch('/partsv2?sku='+filter.sku+'&make_id='+filter.make_id+'&model_id='+filter.model_id+'&year='+filter.year+'&search='+filter.search+'&page='+filter.page, 'GET', true, null)
            if(res.type === 'Success'){
                const new_parts = [...parts, ...res.data.parts];
                setParts(new_parts)
            }else{
                showAlert(res)
            }
            setLoading(false)
        }
        if(filter.page > 1){
            if(filter.sku !== '') getParts()
            if(filter.make_id !== '' && filter.model_id !== '' && filter.year !== '') getParts()
        }
    },[filter.page])
    useLayoutEffect(() => {
        if (!listRef.current || !sentinelRef.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsNearBottom(entry.isIntersecting); },
            { root: listRef.current, threshold: 0, }
        );
        observer.observe(sentinelRef.current);

        return () => observer.disconnect();
    }, [parts_count]);
    useEffect(() => {
        if (isNearBottom) {
            const more = filter.page * 50
            if(more <= parts_count){
                var new_page = filter.page + 1
                setFilter({...filter , page : new_page})
            }
        }
    }, [isNearBottom , parts_count]);

    useEffect(() => {
        const getParts = async() => {
            setLoading(true)
            const res = await handleFetch('/partsv2?sku='+filter.sku+'&search='+filter.search, 'GET', true, null)
            if(res.type === 'Success'){
                setParts(res.data.parts)
                setPartsCount(res.data.count)
            }else{
                showAlert(res)
            }
            setLoading(false)
        }
        if(filter.sku !== ''){
            getParts()
        }
    },[filter.sku])
    const handleCallBack = async ( row : i_parts , type : string) => {
        setTarget(row)
        const res : i_promise = await handleFetch('/part/farm/'+row.sku, 'GET', true, null)
        setTarget(null)
        if(res.type === "Success"){
            const updatedParts : i_parts[] = parts.map(part => { 
                if(part._id === res.data._id){
                    return res.data
                }
                return part
            })
            setParts(updatedParts)
        }
        showAlert(res)
    }
    const handleSkuCallBack = () => {
        setFilter({ year : '' , model_id : '' , sku : sku , make_id : '' , page : 1 , rows_per_page : 25 , order : 'DESC' , disabled : 0 , search : 'initial'})
    }
    const handleScrollTop = () => {
        if (listRef.current?.firstElementChild) {
            (listRef.current.firstElementChild as HTMLElement).scrollIntoView({ behavior: 'smooth' });
        }
    };
    
    const handleSearchCallbacks = async( search : i_part_search) => {
        if(search.sku === ''){
            const res = await findModel(search.model_id)
            if(res.length > 0){
                setYears(res[0].years)
            }
            setSku('')
        }else{
            setSku(search.sku)
        }
        setFilter((prev) => ({
            ...prev,
            make_id: search.make_id,
            search: 'search',
            page : 1
        }));
    
        setTimeout(() => {
            setFilter((prev) => ({
                ...prev,
                model_id: search.model_id,
                page : 1
            }));
        }, 200);
    
        setTimeout(() => {
            setFilter((prev) => ({
                ...prev,
                sku: search.sku,
                page : 1
            }));
        }, 400);
    
        setTimeout(() => {
            setFilter((prev) => ({
                ...prev,
                year: search.year === 0 ? '' : search.year.toString(),
                page : 1
            }));
        }, 600);
    }
    const handleLoadMoreCallbacks = () => {

    }
    const handleReset = () => {
        setFilter(default_search)
        setYears([])
        setParts([])
    }
    const handleAddPart = async( item : i_parts) => {
        const res = await handleFetch('/list/'+item._id +"/Add", "PATCH" , true , null)
        if(res.type !== 'Success'){
            showAlert(res)
        }
    }

    const findMake = () => {
        return makes.find(make => make._id === filter.make_id)?.name || 'Select Make'
    }

    const top_parts_container_props : i_top_container_parts = {
        parts : parts,
        make : findMake(),
        model : models.find(model => model._id === filter.model_id)?.name || 'Select Model',
        year : filter.year || 'Select Year',
        sku : filter.sku,
        count : parts_count
    }
    const sideBottomBarProps : i_side_bar_bottom_props = {
        search : part_search,
        callback : handleSearchCallbacks,
        loadMore : handleLoadMoreCallbacks
    }
    const side_bar_props : i_side_bar_props = {
        makes : makes,
        filter : filter,
        setFilter : setFilter,
        models : models,
        handleReset : handleReset,
        sku : sku,
        setSku : setSku,
        skuCallback : handleSkuCallBack,
        years : years,
        setHide : setHideSide,
        childProps : sideBottomBarProps 
    }
    const part_card_props : i_part_card_props = {
        callback : handleCallBack,
        addPart : handleAddPart,
        path : props.uploadPath + props.path + '/uploads/parts/',
        type : 'catalog'
    }
    const findInTheList = ( part : i_parts ) : boolean => {
        if(props.list){
            const index = props.list.items.findIndex(p => p._id === part._id)
            return index > -1
        }else{
            return false
        }
    }
    const findTarget = (part : i_parts) : boolean => {
        if(target){
            if(part._id === target._id) {
                return true
            }else{
                return false
            }
        }else{
            return false
        }
    }
    useEffect(() => {

    },[props.list , parts , target])
    const renderCardColor = ( index : number ) => {
        const color = index % 2 === 0 
        let colorCode = ''
        if(mode === 'Light'){
            colorCode = color ? '#F5F5F5' : '#d3d3d3'
        } else{
            colorCode = color? '#333333' : '#777777'
        }
        return colorCode
    }
    return(
        <Grid container sx={{ height: '100%'}}>
            <Grid size={{ xs: hideSide ? 1 : 4, md: hideSide ? 0.5 : 3 , xl : hideSide ? 0.5 : 2 ,}} sx={{ height : '100%'}}>
                    { !hideSide ? 
                    <>
                        <SideBar {...side_bar_props}></SideBar>
                    </>
                    :
                    <Box sx={{ backgroundColor : '#F5F5F5' , height : '100%'}}>
                        <IconButton onClick={() => setHideSide(false)}>
                            <OpenInFull/>
                        </IconButton>
                    </Box>
                    }
            </Grid>
            <Grid size={{ xs: hideSide ? 11 : 8, md: hideSide ? 11.5 : 9 , xl : hideSide ? 11.5 : 10 }}>
                {loading && (
                <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(255,255,255,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, pointerEvents: 'none', }} >
                    <CircularProgress size={100} />
                </Box>
                )}
                <>
                {filter.sku !== '' &&
                        <TopPartsContainer {...top_parts_container_props} />
                }
                {filter.make_id !== '' && filter.model_id !== '' && filter.year !== '' && filter.sku === '' &&
                    <TopPartsContainer {...top_parts_container_props} />
                }
                {/* Hack here to permit scroll fits... */}
                    <Box ref={listRef} sx={{ flex : 1 , overflow : 'auto' , height: "calc(100vh - 152px)",  position : 'relative'}}>  
                            <Box>
                                { parts.map((part : i_parts , index : number) => 
                                    <Box key={index} sx={{ backgroundColor: renderCardColor(index) }}>
                                        {user.type === 'user' ?
                                        <UserPartCard in={findInTheList(part)} rendering={false} part={part} filter={filter} {...part_card_props}></UserPartCard> 
                                            :
                                        <PartCard in={findInTheList(part)} rendering={findTarget(part)} part={part} filter={filter} {...part_card_props}></PartCard>
                                        }
                                    </Box>
                                    )}
                                    { parts.length > 15 &&
                                        <Box sx={{ padding : 1 , display : 'flex' , justifyContent : 'center' ,backgroundColor: mode === 'Light' ? "white" : "#222"}}>
                                                <IconButton sx={{ fontSize : 15}} onClick={() => handleScrollTop()}>
                                                    <KeyboardArrowUpIcon/>
                                                </IconButton>
                                        </Box>
                                        
                                    }
                                    {parts.length > 49 && 
                                        <div ref={sentinelRef} style={{ height: "10px", backgroundColor: "#F5F5F5" }}></div>
                                    }
                                    
                            </Box> 
                    </Box>
                    </>
            </Grid>
        </Grid>
    )
}

export default Catalog;